import { useState } from 'react';
import { StanCard, StanTable } from '@/components';
import { copyToClipboard } from '@/lib';
import { useAffiliateLinksColumns } from '../hooks/useAffiliateLinksColumns';
import { TransferLinkModal } from './TransferLinkModal';
import { AffiliateLinkSetupModal } from './AffiliateLinkSetupModal';
import { useModal } from '@/hooks/useModal';
import { useUser } from '@/queries/useUser';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';
import { useCompleteAffiliateSetup } from '../queries/useCompleteAffiliateSetup';
import type { AffiliateLink } from '../types/affiliateLinks.types';
import { EMPTY_STATE } from '../constants/affiliateLinks.constants';

interface AffiliateLinksTableProps {
    links: AffiliateLink[];
    isLoading: boolean;
}

export function AffiliateLinksTable({ links, isLoading }: AffiliateLinksTableProps) {
    const [selectedLink, setSelectedLink] = useState<AffiliateLink | null>(null);
    const [setupLink, setSetupLink] = useState<AffiliateLink | null>(null);
    const transferModal = useModal();
    const setupModal = useModal();
    const { user } = useUser();
    const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;
    const completeSetup = useCompleteAffiliateSetup();

    const handleTransfer = (link: AffiliateLink) => {
        setSelectedLink(link);
        transferModal.open();
    };

    const handleCopyUrl = (url: string) => {
        const urlType = isAffiliateOnlySubscription ? 'Resell URL' : 'Product URL';
        copyToClipboard(url, {
            successMessage: `Copied ${urlType}`,
            errorMessage: 'Failed to copy URL',
        });
    };

    const handleCompleteSetup = (link: AffiliateLink) => {
        setSetupLink(link);
        setupModal.open();
    };

    const handleAgreeToTerms = async () => {
        if (!setupLink) return;

        await completeSetup.mutateAsync(setupLink.id);
        setupModal.close();
        setSetupLink(null);
    };

    const columns = useAffiliateLinksColumns({
        onTransfer: handleTransfer,
        onCopyUrl: handleCopyUrl,
        onCompleteSetup: handleCompleteSetup,
        setupLoadingLinkId: completeSetup.isPending && setupLink ? setupLink.id : null,
    });

    return (
        <>
            <StanCard>
                <StanTable
                    columns={columns}
                    data={links}
                    isLoading={isLoading}
                    emptyHeader={EMPTY_STATE.NO_LINKS.HEADER}
                    emptySubheader={EMPTY_STATE.NO_LINKS.SUBHEADER}
                />
            </StanCard>

            <TransferLinkModal isOpen={transferModal.isOpen} onClose={transferModal.close} link={selectedLink} />
            <AffiliateLinkSetupModal
                isOpen={setupModal.isOpen}
                onClose={() => {
                    setupModal.close();
                    setSetupLink(null);
                }}
                terms={setupLink?.affiliate_terms_and_condition || ''}
                linkId={setupLink?.id || 0}
                onAgree={handleAgreeToTerms}
                isLoading={completeSetup.isPending}
            />
        </>
    );
}
